import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Input, TextSelect, Toggle } from '@app/form';
import { SquareButton, useBoolModalState } from '@app/core';
import { ProductType } from 'queries';
import {
  ProductAutocomple,
  ProductOption,
} from 'components/AdminPriceEdition/ProductAutocomplete';
import { toEuros } from 'lib/numbers';
import { ChangeEvent } from 'preact/compat';
import { productTitle, QuoteLine, uid } from './types';

const useStyles = createUseStyles((theme: Theme) => ({
  line: {
    ...theme.fonts.caption,
    color: theme.colors.inkPrimary,
  },
  title: {
    ...theme.fonts.label,
    textAlign: 'left',
  },
  price: {
    ...theme.fonts.caption,
    textAlign: 'center',
    width: 120,
  },
  productSelection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase,
  },
  productType: {
    width: 190,
  },
  addButtonLine: {
    position: 'relative',
    '& td.no-height-line': {
      height: 0,
      padding: 0,
      borderBottom: {
        color: theme.colors.green,
        style: 'dashed',
        width: 1,
      },
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
  },
  addButton: {
    position: 'absolute',
    top: -theme.marginBase * 2,
    left: '50%',
    right: '50%',
    border: {
      color: theme.colors.green,
      style: 'solid',
      width: 1,
    },
  },
}));

interface Values {
  option?: ProductOption;
  title?: string;
  price?: number | null;
  quantity?: number;
  pricePerParticipant?: boolean;
  productId?: string;
}

interface Props {
  duration: number;
  numberOfParticipants: number;
  locale: string;
  onAdd: (line: QuoteLine) => void;
}

export const AddQuoteProductLine = ({
  duration,
  numberOfParticipants,
  locale,
  onAdd,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [adding, startAdding, stopAdding] = useBoolModalState(false);
  const [values, setValues] = useState<Values>({});

  const [productType, setProductType] = useState<ProductType>(
    ProductType.FEATURE,
  );
  const [perParticipant, setPerParticipant] = useState(false);

  const onProductChange = useCallback(
    (data: ProductOption) => {
      const details = data.details;
      const perUnit = details ? !!details.pricePerParticipant : perParticipant;

      setValues({
        option: data,
        productId: details?.id,
        title: details ? productTitle(details) : data.value,
        price: details ? details.price : 0,
        pricePerParticipant: perUnit,
        quantity: perUnit ? numberOfParticipants : 1,
      });
    },
    [numberOfParticipants, perParticipant],
  );

  const onPerParticipantToggle = useCallback(
    (checked: boolean) => {
      setPerParticipant(checked);
      setValues((curr) =>
        curr.option && !curr.option.details
          ? {
              ...curr,
              pricePerParticipant: checked,
              quantity: checked ? numberOfParticipants : 1,
            }
          : curr,
      );
    },
    [numberOfParticipants],
  );

  const onPriceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = (event.currentTarget || event.target).value;
      setValues((curr) => ({
        ...curr,
        price: value !== '' ? Math.round(Number(value) * 100) || 0 : null,
      }));
    },
    [setValues],
  );

  const onQuantityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = (event.currentTarget || event.target).value;
      setValues((curr) => ({ ...curr, quantity: Number(value) || 0 }));
    },
    [setValues],
  );

  const save = useCallback(() => {
    if (!values.option) {
      return;
    }
    const details = values.option.details;
    const title = values.title || values.option.value;
    const unitPrice = values.price || 0;
    const edited =
      !details ||
      title !== productTitle(details) ||
      unitPrice !== details.price;

    onAdd({
      id: uid(),
      productId: values.productId,
      productName: details ? String(details.name) : undefined,
      title,
      unitPrice,
      quantity: values.quantity || 0,
      pricePerParticipant: values.pricePerParticipant,
      edited,
    });
    setValues({});
    stopAdding();
  }, [values, onAdd, stopAdding]);

  const discard = useCallback(() => {
    setValues({});
    stopAdding();
  }, [stopAdding]);

  if (!adding) {
    return (
      <tr className={classes.addButtonLine}>
        <td colSpan={5} className="no-height-line">
          <SquareButton
            icon="ri-add-line"
            color="green"
            small
            className={classes.addButton}
            onClick={startAdding}
          />
        </td>
      </tr>
    );
  }

  const price = values.price || 0;
  const quantity = values.quantity || 0;

  return (
    <tr className={classes.line}>
      <td className={classes.title}>
        <div className={classes.productSelection}>
          <TextSelect
            noMargin
            containerClass={classes.productType}
            options={Object.values(ProductType)}
            value={productType}
            onChange={(type) => setProductType(type as ProductType)}
          />
          <Toggle
            noMargin
            checked={perParticipant}
            onToggle={onPerParticipantToggle}
            label={t('quote.perParticipant')}
          />
        </div>
        <ProductAutocomple
          productType={productType}
          pricePerParticipant={perParticipant}
          duration={duration}
          locale={locale}
          placeholder={t('quote.addProduct')}
          onChange={onProductChange}
          value={values.option}
        />
      </td>
      <td className={classes.price}>
        <Input
          type="number"
          noMargin
          value={values.price || values.price === 0 ? price / 100 : null}
          onChange={onPriceChange}
        />
      </td>
      <td className={classes.price}>
        <Input
          type="number"
          noMargin
          value={quantity}
          disabled={!!values.pricePerParticipant}
          onChange={onQuantityChange}
        />
      </td>
      <td className={classes.price}>{toEuros(price * quantity)}</td>
      <td>
        <div className={classes.actions}>
          <SquareButton
            icon="ri-check-line"
            color="green"
            small
            variant="plain"
            onClick={save}
          />
          <SquareButton
            icon="ri-delete-bin-line"
            color="red"
            small
            variant="transparent"
            onClick={discard}
          />
        </div>
      </td>
    </tr>
  );
};
